import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "YOUR_SUPABASE_URL";
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY || "YOUR_SUPABASE_ANON_KEY";

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Log presence of Supabase env values in a safe way (masked).
function _maskSecret(value: string | undefined) {
  if (!value) return "<missing>";
  if (value.length <= 8) return "****";
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
}


// Database Types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: "investor" | "admin";
  phone?: string;
  avatar_url?: string;
  created_at: string;
  updated_at: string;
}

export interface InvestorProfile {
  id: string;
  user_id: string;
  risk_tolerance: "conservative" | "moderate" | "aggressive";
  investment_experience: "beginner" | "intermediate" | "advanced" | "expert";
  preferred_sectors: string[];
  minimum_investment: number;
  maximum_investment: number;
  investment_goals: string[];
  tax_jurisdiction: string;
  accredited_investor: boolean;
  net_worth_range: string;
  annual_income_range: string;
  notifications_enabled: boolean;
  two_factor_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface Investment {
  id: string;
  investor_id: string;
  deal_id: string;
  activo_id?: string;
  amount: number;
  investment_date: string;
  status: "active" | "pending" | "completed" | "cancelled";
  expected_return: number;
  actual_return?: number;
  maturity_date: string;
  created_at: string;
  updated_at: string;
}

export interface Deal {
  id: string;
  title: string;
  description: string;
  category:
    | "real_estate"
    | "private_equity"
    | "venture_capital"
    | "debt"
    | "other";
  minimum_investment: number;
  maximum_investment: number;
  expected_return: number;
  risk_level: "low" | "medium" | "high";
  duration_months: number;
  status: "open" | "closed" | "funding" | "completed";
  total_funding_goal: number;
  current_funding: number;
  closing_date: string;
  documents: string[];
  images: string[];
  manager_id: string;
  created_at: string;
  updated_at: string;
}

export interface Portfolio {
  id: string;
  investor_id: string;
  total_invested: number;
  current_value: number;
  total_return: number;
  return_percentage: number;
  last_updated: string;
}

export interface Transaction {
  id: string;
  investor_id: string;
  investment_id?: string;
  activo_id?: string;
  type: "deposit" | "withdrawal" | "dividend" | "fee" | "interest";
  amount: number;
  status: "pending" | "completed" | "failed";
  description: string;
  transaction_date: string;
  created_at: string;
}

export interface Fee {
  id: number;
  concepto: string;
  cantidad: number;
  fecha: string; // YYYY-MM-DD
  estado: "PENDING" | "PAID";
  fecha_pago?: string | null;
  link?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Report {
  id: string;
  investor_id: string;
  type: "monthly" | "quarterly" | "annual" | "custom";
  title: string;
  content: any; // JSON data
  file_url?: string;
  generated_at: string;
  period_start: string;
  period_end: string;
}

export interface CapitalContribution {
  id: string;
  investor_id: string;
  amount: number;
  capital_percentage: number;
  contribution_date: string;
  note?: string | null;
  link?: string | null;
  created_at: string;
  updated_at: string;
}

export interface NavGeneral {
  id: string;
  total_acquisition_cost: number | null;
  total_asset_value: number | null;
  annual_rents: number | null;
  cash: number | null;
  equivalents: number | null;
  mortgages: number | null;
  bullet_loans: number | null;
  cash_on_cash?: number | null;
  roi?: number | null;
  value: number | null;
  period_start: string | null;
  period_end: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CapitalStructureRow {
  investor_id: string;
  total_amount: number;
  total_percentage: number;
}

// Supabase Database Functions
export const db = {
  // Auth: password reset request and completion
  async requestPasswordReset(email: string, redirectTo?: string) {
    const redirect =
      redirectTo ||
      import.meta.env.VITE_PASSWORD_RESET_REDIRECT_URL ||
      undefined;

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirect,
    });

    if (error) throw error;
    return data;
  },

  async completePasswordReset(newPassword: string) {
    // When the user visits the magic link, Supabase sets a recovery session.
    // We only need to call updateUser with the new password.
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
    return data;
  },
  // User operations
  async getUser(userId: string) {
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (error) throw error;
    return data as User;
  },

  // create user function
  async createUser(
    user: Omit<User, "id" | "created_at" | "updated_at">,
    initialInvestment?: Partial<
      Omit<Investment, "id" | "created_at" | "updated_at" | "investor_id">
    >
  ) {
    // Create user, then create a minimal investor_profile and optionally an investment
    // If any step fails, attempt to rollback the previous inserts to avoid partial state.
    const now = new Date().toISOString();
    // 1) create user
    const { data: userData, error: userError } = await supabase
      .from("users")
      .insert({ ...user, created_at: now, updated_at: now })
      .select()
      .single();

    if (userError) throw userError;
    const createdUser = userData as User;

    // 2) create investor profile (minimal/defaults)
    let createdProfile: any = null;
    try {
      const profilePayload: Partial<InvestorProfile> = {
        user_id: createdUser.id,
        // leave optional fields empty so DB defaults / checks apply
        minimum_investment: 0,
        maximum_investment: null as unknown as number,
        preferred_sectors: [],
        investment_goals: [],
        accredited_investor: false,
        notifications_enabled: true,
        created_at: now,
        updated_at: now,
      };

      const { data: profileData, error: profileError } = await supabase
        .from("investor_profiles")
        .insert(profilePayload)
        .select()
        .single();

      if (profileError) throw profileError;
      createdProfile = profileData;
    } catch (e) {
      // rollback user
      try {
        await supabase.from("users").delete().eq("id", createdUser.id);
      } catch (_) {
        // swallow rollback errors
      }
      throw e;
    }

    // 3) optionally create an initial investment (and transaction)
    if (initialInvestment) {
      let createdInvestment: any = null;
      try {
        const investmentPayload: Partial<Investment> = {
          investor_id: createdUser.id,
          deal_id: initialInvestment.deal_id ?? undefined,
          activo_id: (initialInvestment as any).activo_id ?? undefined,
          amount: initialInvestment.amount || 0,
          investment_date: initialInvestment.investment_date || now,
          status: initialInvestment.status || "active",
          expected_return: initialInvestment.expected_return || 0,
          actual_return: initialInvestment.actual_return ?? undefined,
          maturity_date: initialInvestment.maturity_date || now,
          created_at: now,
          updated_at: now,
        };

        const { data: investData, error: investError } = await supabase
          .from("investments")
          .insert(investmentPayload)
          .select()
          .single();

        if (investError) throw investError;
        createdInvestment = investData;

        // If amount > 0 create a deposit transaction reflecting the initial funding
        const amt = Number(investmentPayload.amount || 0);
        if (amt > 0) {
          const txPayload = {
            investor_id: createdUser.id,
            investment_id: createdInvestment.id,
            activo_id: investmentPayload.activo_id ?? undefined,
            type: "deposit",
            amount: amt,
            status: "completed",
            description: "Initial investment deposit",
            transaction_date: now,
            created_at: now,
          };

          const { error: txError } = await supabase
            .from("transactions")
            .insert(txPayload);

          if (txError) throw txError;
        }
      } catch (e) {
        // rollback investment (if created), profile and user
        try {
          if (createdInvestment && createdInvestment.id) {
            await supabase
              .from("investments")
              .delete()
              .eq("id", createdInvestment.id);
          }
          if (createdProfile && createdProfile.id) {
            await supabase
              .from("investor_profiles")
              .delete()
              .eq("id", createdProfile.id);
          }
          await supabase.from("users").delete().eq("id", createdUser.id);
        } catch (_err) {
          // swallow rollback errors
        }
        throw e;
      }
    }

    return createdUser as User;
  },

  async mockGetUser(userId: string): Promise<User> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          id: userId,
          email: "example@example.com",
          first_name: "John",
          last_name: "Doe",
          user_type: "investor",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }, 500); // Simulate network delay
    });
  },

  async updateUser(userId: string, updates: Partial<User>) {
    // Defensive: use maybeSingle to avoid coercion errors and provide clearer messages
    try {
      // Log payload shape for debugging (non-sensitive)
      // eslint-disable-next-line no-console
      console.debug("updateUser payload:", { userId, updates });

      const { data, error } = await supabase
        .from("users")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", userId)
        .select()
        .maybeSingle();

      if (error) {
        console.debug("Supabase updateUser error:", error);
        // Re-throw Supabase errors to caller
        throw error;
      }

      if (!data) {
        throw new Error(`No user found/updated for id=${userId}`);
      }

      return data as User;
    } catch (err: any) {
      // Attach context to the error for easier debugging upstream
      // eslint-disable-next-line no-console
      console.error("updateUser failed", {
        userId,
        updates,
        err: err?.message || err,
      });
      throw err;
    }
  },

  // Investor Profile operations
  async getInvestorProfile(userId: string) {
    const { data, error } = await supabase
      .from("investor_profiles")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error) throw error;
    return data as InvestorProfile;
  },

  async updateInvestorProfile(
    userId: string,
    updates: Partial<InvestorProfile>
  ) {
    const { data, error } = await supabase
      .from("investor_profiles")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .select()
      .single();

    if (error) throw error;
    return data as InvestorProfile;
  },

  // Investment operations
  async getInvestments(investorId: string) {
    const { data, error } = await supabase
      .from("investments")
      .select(
        `
        *,
        deals (
          title,
          category,
          expected_return,
          risk_level
        )
      `
      )
      .eq("investor_id", investorId)
      .order("investment_date", { ascending: false });

    if (error) throw error;
    return data;
  },

  async createInvestment(
    investment: Omit<Investment, "id" | "created_at" | "updated_at">
  ) {
    const { data, error } = await supabase
      .from("investments")
      .insert({
        ...investment,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as Investment;
  },

  // Deal operations
  async getDeals(status?: string) {
    let query = supabase
      .from("deals")
      .select("*")
      .order("created_at", { ascending: false });

    if (status) {
      query = query.eq("status", status);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as Deal[];
  },

  async getDeal(dealId: string) {
    const { data, error } = await supabase
      .from("deals")
      .select("*")
      .eq("id", dealId)
      .single();

    if (error) throw error;
    return data as Deal;
  },

  // Portfolio operations
  async getPortfolio(investorId: string) {
    // Select explicit columns to avoid '*' content-negotiation issues with PostgREST
    const { data, error } = await supabase
      .from("portfolios")
      .select("id, investor_id, total_invested, current_value, total_return, return_percentage, last_updated")
      .eq("investor_id", investorId)
      .maybeSingle();

    if (error) throw error;
    return data as Portfolio;
  },

  async updatePortfolio(investorId: string, updates: Partial<Portfolio>) {
    const { data, error } = await supabase
      .from("portfolios")
      .update({ ...updates, last_updated: new Date().toISOString() })
      .eq("investor_id", investorId)
      .select()
      .single();

    if (error) throw error;
    return data as Portfolio;
  },

  // Transaction operations
  async getTransactions(investorId: string, limit = 50) {
    const { data, error } = await supabase
      .from("transactions")
      .select("*")
      .eq("investor_id", investorId)
      .order("transaction_date", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data as Transaction[];
  },

  async createTransaction(transaction: Omit<Transaction, "id" | "created_at">) {
    const { data, error } = await supabase
      .from("transactions")
      .insert({
        ...transaction,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as Transaction;
  },

  // Capital contributions (RPC wrappers)
  async addContribution(payload: {
    investor_id: string;
    amount: number;
    capital_percentage: number;
    contribution_date: string;
    note?: string | null;
    link?: string | null;
  }) {
    // Try existing RPC first for backward compatibility. If the RPC doesn't accept
    // a `p_link` parameter (or fails), fall back to a direct insert so the optional
    // `link` column is persisted once the DB migration has been applied.
    const rpcParams: any = {
      p_investor_id: payload.investor_id,
      p_amount: payload.amount,
      p_capital_percentage: payload.capital_percentage,
      p_contribution_date: payload.contribution_date,
      p_note: payload.note ?? null,
    };
    if (payload.link !== undefined) rpcParams.p_link = payload.link ?? null;

    const { data, error } = await supabase.rpc("add_contribution", rpcParams as any);
    if (!error && data) return data as CapitalContribution;

    // Fallback: insert directly into table (handles cases where RPC isn't updated)
    const insertPayload: any = {
      investor_id: payload.investor_id,
      amount: payload.amount,
      capital_percentage: payload.capital_percentage,
      contribution_date: payload.contribution_date,
      note: payload.note ?? null,
    };
    if (payload.link !== undefined) insertPayload.link = payload.link ?? null;

    const { data: insData, error: insErr } = await supabase
      .from("capital_contributions")
      .insert(insertPayload)
      .select()
      .single();

    if (insErr) throw insErr;
    return insData as CapitalContribution;
  },

  async getContributionsByInvestor(investorId: string) {
    const { data, error } = await supabase.rpc(
      "get_contributions_by_investor",
      {
        p_investor_id: investorId,
      }
    );

    if (error) throw error;
    return data as CapitalContribution[];
  },

  async updateContribution(payload: {
    contribution_id: string;
    amount: number;
    capital_percentage: number;
    note?: string | null;
    link?: string | null;
  }) {
    const rpcParams: any = {
      p_contribution_id: payload.contribution_id,
      p_amount: payload.amount,
      p_capital_percentage: payload.capital_percentage,
      p_note: payload.note ?? null,
    };
    if (payload.link !== undefined) rpcParams.p_link = payload.link ?? null;

    const { data, error } = await supabase.rpc("update_contribution", rpcParams as any);
    if (!error && data) return data as CapitalContribution;

    // Fallback to direct update if RPC isn't available/updated
    const updatePayload: any = {
      amount: payload.amount,
      capital_percentage: payload.capital_percentage,
      note: payload.note ?? null,
    };
    if (payload.link !== undefined) updatePayload.link = payload.link ?? null;

    const { data: updData, error: updErr } = await supabase
      .from("capital_contributions")
      .update(updatePayload)
      .eq("id", payload.contribution_id)
      .select()
      .single();

    if (updErr) throw updErr;
    return updData as CapitalContribution;
  },

  async deleteContribution(contributionId: string) {
    const { data, error } = await supabase.rpc("delete_contribution", {
      p_contribution_id: contributionId,
    });

    if (error) throw error;
    return data as boolean;
  },

  async getCapitalStructure() {
    const { data, error } = await supabase.rpc("get_capital_structure");
    if (error) throw error;
    return data as CapitalStructureRow[];
  },

  async recomputeCapitalStructure() {
    const { data, error } = await supabase.rpc("recompute_capital_structure");
    if (error) throw error;
    return data as CapitalStructureRow[];
  },

  // nav_general RPC wrappers
  async createNavMonth(payload: {
    total_acquisition_cost?: number;
    total_asset_value?: number;
    annual_rents?: number;
    cash?: number;
    equivalents?: number;
    mortgages?: number;
    bullet_loans?: number;
    cash_on_cash?: number;
    roi?: number;
    value?: number;
    period_month: string; // ISO date (YYYY-MM-DD)
  }) {
    // Normalize period_month to month-start 'YYYY-MM-01' to avoid
    // duplicates due to timezone or differing ISO formats (YYYY-MM, YYYY-MM-DD, or full Z timestamps)
    const raw = payload.period_month || "";
    // Take YYYY-MM from the provided string (handles 'YYYY-MM', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:MM:SSZ')
    const ym = raw.slice(0, 7);
    const periodStart = `${ym}-01`;

    const { data, error } = await supabase.rpc("create_nav_month", {
      p_total_acquisition_cost: payload.total_acquisition_cost ?? null,
      p_total_asset_value: payload.total_asset_value ?? null,
      p_annual_rents: payload.annual_rents ?? null,
      p_cash: payload.cash ?? null,
      p_equivalents: payload.equivalents ?? null,
      p_mortgages: payload.mortgages ?? null,
      p_bullet_loans: payload.bullet_loans ?? null,
      p_cash_on_cash: payload.cash_on_cash ?? null,
      p_roi: payload.roi ?? null,
      p_nav_value: payload.value ?? null,
      p_period_start: periodStart,
      p_period_end: periodStart,
    });

    if ((error as any) ?? false) throw error;
    return data as NavGeneral;
  },

  async updateNavMonth(payload: {
    id: string;
    total_acquisition_cost?: number;
    total_asset_value?: number;
    annual_rents?: number;
    cash?: number;
    equivalents?: number;
    mortgages?: number;
    bullet_loans?: number;
    cash_on_cash?: number;
    roi?: number;
    value?: number;
  }) {
    const { data, error } = await supabase.rpc("update_nav_month", {
      p_id: payload.id,
      p_total_acquisition_cost: payload.total_acquisition_cost ?? null,
      p_total_asset_value: payload.total_asset_value ?? null,
      p_annual_rents: payload.annual_rents ?? null,
      p_cash: payload.cash ?? null,
      p_equivalents: payload.equivalents ?? null,
      p_mortgages: payload.mortgages ?? null,
      p_bullet_loans: payload.bullet_loans ?? null,
      p_cash_on_cash: payload.cash_on_cash ?? null,
      p_roi: payload.roi ?? null,
      p_nav_value: payload.value ?? null,
    });

    if ((error as any) ?? false) throw error;
    return data as NavGeneral;
  },

  async deleteNavMonth(id: string) {
    const { data, error } = await supabase.rpc("delete_nav_month", {
      p_id: id,
    });
    if (error) throw error;
    return data as boolean;
  },

  async getNavMonths(periodStart?: string | null, periodEnd?: string | null) {
    const { data, error } = await supabase.rpc("get_nav_months", {
      p_period_start: periodStart ?? null,
      p_period_end: periodEnd ?? null,
    });


    if (error) throw error;
    return data as NavGeneral[];
  },

  async getNavMonthsPaged(page: number, pageSize: number) {
    const from = Math.max(0, (page - 1) * pageSize);
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from("nav_general")
      .select("*", { count: "exact" })
      .order("period_month", { ascending: false })
      .range(from, to);

    if (error) throw error;
    return { data: (data || []) as NavGeneral[], total: count ?? 0 };
  },

  // Helper: fetch all investors from the DB view (includes profile fields)
  async getInvestors() {
    const { data, error } = await supabase
      .from("investors_full_view")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as (User &
      Partial<InvestorProfile> & { investor_id: string })[];
  },

  async getInvestorsPaged(page: number, pageSize: number, search?: string) {
    const from = Math.max(0, (page - 1) * pageSize);
    const to = from + pageSize - 1;

    let query = supabase
      .from("investors_full_view")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (search && search.trim()) {
      // Search across email and names
      query = query.or(
        `email.ilike.%${search.trim()}%,first_name.ilike.%${search.trim()}%,last_name.ilike.%${search.trim()}%`
      );
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return {
      data: (data || []) as (User &
        Partial<InvestorProfile> & { investor_id: string })[],
      total: count ?? 0,
    };
  },

  // Helper: fetch all capital contributions in bulk
  async getAllContributions() {
    const { data, error } = await supabase
      .from("capital_contributions")
      .select(
        "id, investor_id, amount, capital_percentage, contribution_date, note"
      );


    if (error) throw error;
    return data as CapitalContribution[];
  },

  async getContributionsPaged(
    investorId: string | undefined,
    page: number,
    pageSize: number
  ) {
    const from = Math.max(0, (page - 1) * pageSize);
    const to = from + pageSize - 1;

    let query = supabase
      .from("capital_contributions")
      .select(
        "id, investor_id, amount, capital_percentage, contribution_date, note",
        { count: "exact" }
      )
      .order("contribution_date", { ascending: false })
      .range(from, to);

    if (investorId) {
      query = query.eq("investor_id", investorId);
    }


    const { data, error, count } = await query;
    if (error) throw error;
    return { data: (data || []) as CapitalContribution[], total: count ?? 0 };
  },

  // Report operations
  async getReports(investorId: string) {
    const { data, error } = await supabase
      .from("reports")
      .select("*")
      .eq("investor_id", investorId)
      .order("generated_at", { ascending: false });

    if (error) throw error;
    return data as Report[];
  },

  async generateReport(
    investorId: string,
    type: Report["type"],
    periodStart: string,
    periodEnd: string
  ) {
    // This would typically involve complex calculations
    const { data, error } = await supabase
      .from("reports")
      .insert({
        investor_id: investorId,
        type,
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Report`,
        content: {}, // Would be populated with actual report data
        generated_at: new Date().toISOString(),
        period_start: periodStart,
        period_end: periodEnd,
      })
      .select()
      .single();

    if (error) throw error;
    return data as Report;
  },

  // Activos Inmobiliarios operations (for admin)
  async getActivos(searchName?: string) {
    let query = supabase
      .from("activos_inmobiliarios")
      .select("*")
      .order("created_at", { ascending: false });

    if (searchName && searchName.trim().length > 0) {
      // Case-insensitive contains on nombre
      query = query.ilike("nombre", `%${searchName.trim()}%`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getActivosPaged(
    searchName: string | undefined,
    page: number,
    pageSize: number
  ) {
    const from = Math.max(0, (page - 1) * pageSize);
    const to = from + pageSize - 1;

    let query = supabase
      .from("activos_inmobiliarios")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (searchName && searchName.trim().length > 0) {
      query = query.ilike("nombre", `%${searchName.trim()}%`);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], total: count ?? 0 };
  },

  async createActivo(activo: any) {
    const { data, error } = await supabase
      .from("activos_inmobiliarios")
      .insert(activo)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateActivo(id: string, updates: any) {
    const { data, error } = await supabase
      .from("activos_inmobiliarios")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteActivo(id: string) {
    const { error } = await supabase
      .from("activos_inmobiliarios")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return true;
  },

  // Fees CRUD (similar to activos handlers)
  async getFees(limit = 100) {
    const { data, error } = await supabase
      .from("fees")
      .select("*")
      .order("fecha", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data as Fee[];
  },

  async getFeesPaged(page: number, pageSize: number, estado?: string) {
    const from = Math.max(0, (page - 1) * pageSize);
    const to = from + pageSize - 1;

    let query: any = supabase
      .from("fees")
      .select("*", { count: "exact" })
      .order("fecha", { ascending: false })
      .range(from, to);

    if (estado && estado.trim().length > 0) {
      query = query.eq("estado", estado);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: data || [], total: count ?? 0 };
  },

  async getFeesTotal() {
    // Returns the total sum of `cantidad` from the `fees` table.
    try {
      const { data, error } = await supabase.from("fees").select("cantidad");
      if (error) throw error;
      const total = (data || []).reduce((acc: number, row: any) => acc + Number(row.cantidad || 0), 0);
      return total;
    } catch (err) {
      throw err;
    }
  },

  async getFeesTotalsByQuarter(referenceDate?: string, includePending = true) {
    // Returns totals for the current quarter and the previous quarter
    // referenceDate (ISO) can be provided for deterministic testing
    const ref = referenceDate ? new Date(referenceDate) : new Date();

    const getQuarterRange = (d: Date) => {
      const year = d.getFullYear();
      const month = d.getMonth();
      const quarterStartMonth = Math.floor(month / 3) * 3; // 0,3,6,9
      const start = new Date(year, quarterStartMonth, 1);
      const end = new Date(year, quarterStartMonth + 3, 0); // last day of quarter
      const toIso = (dt: Date) => dt.toISOString().slice(0, 10); // YYYY-MM-DD
      return { start: toIso(start), end: toIso(end) };
    };

    const current = getQuarterRange(ref);
    const prevRef = new Date(ref.getFullYear(), ref.getMonth() - 3, 1);
    const previous = getQuarterRange(prevRef);

    try {
      const buildQuery = (range: { start: string; end: string }) => {
        let q: any = supabase.from("fees").select("cantidad").gte("fecha", range.start).lte("fecha", range.end);
        if (includePending) {
          q = q.in("estado", ["PAID", "PENDING"]);
        } else {
          q = q.eq("estado", "PAID");
        }
        return q;
      };

      const [{ data: curRows, error: curErr } = { data: [], error: null }, { data: prevRows, error: prevErr } = { data: [], error: null }] = await Promise.all([
        buildQuery(current),
        buildQuery(previous),
      ] as any);

      if (curErr) throw curErr;
      if (prevErr) throw prevErr;

      const sum = (rows: any[]) => (rows || []).reduce((acc: number, r: any) => acc + Number(r.cantidad || 0), 0);

      return {
        currentTotal: sum(curRows || []),
        previousTotal: sum(prevRows || []),
        currentRange: current,
        previousRange: previous,
      };
    } catch (err) {
      throw err;
    }
  },

  async createFee(fee: Omit<Fee, "id" | "created_at" | "updated_at">) {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("fees")
      .insert({ ...fee, created_at: now, updated_at: now })
      .select()
      .single();

    if (error) throw error;
    return data as Fee;
  },

  async updateFee(id: number, updates: Partial<Fee>) {
    const { data, error } = await supabase
      .from("fees")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as Fee;
  },

  async deleteFee(id: number) {
    const { error } = await supabase.from("fees").delete().eq("id", id);
    if (error) throw error;
    return true;
  },

  // KPI Historicos operations (for admin)
  async getKPIHistoricos() {
    const { data, error } = await supabase
      .from("kpi_historicos")
      .select("*")
      .order("fecha", { ascending: false });

    if (error) throw error;
    return data;
  },

  async createKPI(kpi: any) {
    const { data, error } = await supabase
      .from("kpi_historicos")
      .insert(kpi)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateKPI(id: string, updates: any) {
    const { data, error } = await supabase
      .from("kpi_historicos")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteKPI(id: string) {
    const { error } = await supabase
      .from("kpi_historicos")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return true;
  },
  // Crear inversor completo usando la lógica del ejemplo proporcionado
  async createInvestorCompleto(formData: {
    nombre: string;
    email: string;
    password: string;
    capitalInvertido?: number;
    fechaInversion?: string;
    porcentajeCapitalSocial?: number;
    telefono?: string;
    link?: string | null;
  }) {
    try {
      // Validaciones
      if (!formData.nombre || !formData.email || !formData.password) {
        throw new Error("Nombre, email y contraseña son obligatorios");
      }

      if (formData.password.length < 6) {
        throw new Error("La contraseña debe tener al menos 6 caracteres");
      }

      // Separar nombre
      const nombreParts = formData.nombre.trim().split(" ");
      const first_name = nombreParts[0];
      const last_name = nombreParts.slice(1).join(" ") || nombreParts[0];

      // PASO 2: Crear usuario usando Edge Function admin-create-user
      // This uses the current session's access_token in the Authorization header
      // Flatten the payload so the Edge Function receives the expected top-level fields
      const userPayload = {
        email: formData.email,
        password: formData.password,
        first_name,
        last_name,
        user_type: "investor",
        phone: formData.telefono || null,
      };

      const createdUser = await db.createUserAdmin(userPayload);
      // Edge function may return the created user under different shapes; try both
      const userId = createdUser?.user?.id || createdUser?.id;
      if (!userId) {
        throw new Error(
          `No se obtuvo userId desde la función: ${JSON.stringify(createdUser)}`
        );
      }

      // Use DB RPC to perform atomic inserts server-side
      const capitalInicial = formData.capitalInvertido || 0;
      const fechaInversion = formData.fechaInversion
        ? new Date(formData.fechaInversion)
        : new Date();

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "create_investor_complete",
        {
          p_user_id: userId,
          p_email: formData.email,
          p_first_name: first_name,
          p_last_name: last_name,
          p_phone: formData.telefono || null,
          p_capital_invertido: capitalInicial,
          p_fecha_inversion: fechaInversion.toISOString(),
          p_porcentaje_capital_social: formData.porcentajeCapitalSocial || 0,
          p_link: formData.link || null,
        }
      );

      if (rpcError) {
        // Attempt to delete the auth user to avoid orphaned auth accounts (requires service role)
        try {
          if (userId && (supabase.auth as any)?.admin?.deleteUser) {
            await db.adminDeleteUser(userId);
          }
        } catch (_) {
          // swallow — caller should run cleanup if needed
        }
        throw new Error(`Error creating DB records: ${rpcError.message}`);
      }

      return {
        success: true,
        userId: userId,
        email: formData.email,
        nombre: formData.nombre,
        capitalInvertido: capitalInicial,
        message: "Inversor creado exitosamente",
        rpcData,
      };
    } catch (error: any) {
      console.error("Error al crear inversor:", error);
      return {
        success: false,
        error: error.message || "Error desconocido al crear inversor",
      };
    }
  },

  // Call an Edge Function to create a user as admin.
  // The function is called with the current session access_token in Authorization header.
  async createUserAdmin(payload: any) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const token = session?.access_token;
    if (!token) {
      throw new Error(
        "No active session found. You must be logged in as an admin to call this function."
      );
    }

    const url = `${supabaseUrl}/functions/v1/admin-create-user`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, // Aquí solo pasa el JWT
      },
      body: JSON.stringify(payload),
    });

    const text = await res.text();
    let json: any;
    try {
      json = text ? JSON.parse(text) : null;
    } catch (e) {
      json = text;
    }

    if (!res.ok) {
      const msg =
        (json && (json.message || json.error)) ||
        `Edge function returned ${res.status}`;
      // Attach the raw response for easier debugging upstream
      const error = new Error(msg);
      (error as any).response = json;
      throw error;
    }

    return json;
  },

  // Call an Edge Function to delete a user as admin.
  // Usage: await db.adminDeleteUser(userId) -- requires the caller to be an admin (session access_token).
  async adminDeleteUser(userId: string) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const token = session?.access_token;
    if (!token) {
      throw new Error(
        "No active session found. You must be logged in as an admin to call this function."
      );
    }

    const url = `${supabaseUrl}/functions/v1/admin-delete-user`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ user_id: userId }),
    });

    const text = await res.text();
    let json: any;
    try {
      json = text ? JSON.parse(text) : null;
    } catch (e) {
      json = text;
    }

    if (!res.ok) {
      const msg =
        (json && (json.message || json.error)) ||
        `Edge function returned ${res.status}`;
      const error = new Error(msg);
      (error as any).response = json;
      throw error;
    }

    return json;
  },

  // Call an Edge Function to change a user's password as admin.
  // Usage: await db.adminChangePassword(userId, newPassword)
  // Requires the caller to be logged in as admin (session access_token passed to Edge Function).
  async adminChangePassword(userId: string, newPassword: string) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const token = session?.access_token;
    if (!token) {
      throw new Error(
        "No active session found. You must be logged in as an admin to call this function."
      );
    }

    const url = `${supabaseUrl}/functions/v1/admin-update-password`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ user_id: userId, new_password: newPassword }),
    });

    const text = await res.text();
    let json: any;
    try {
      json = text ? JSON.parse(text) : null;
    } catch (e) {
      json = text;
    }

    if (!res.ok) {
      const msg = (json && (json.message || json.error)) || `Edge function returned ${res.status}`;
      const error = new Error(msg);
      (error as any).response = json;
      throw error;
    }

    return json;
  },

  async deleteInvestor(userId: string): Promise<boolean> {
    try {
      // Ensure user exists
      const { data: existing, error: findErr } = await supabase
        .from("users")
        .select("id")
        .eq("id", userId)
        .maybeSingle();

      if (findErr) throw findErr;
      if (!existing) throw new Error(`User not found: ${userId}`);

      // Delete dependent DB records (best-effort, fail on first error to avoid partial state)
      const deletes = [
        supabase.from("transactions").delete().eq("investor_id", userId),
        supabase.from("investments").delete().eq("investor_id", userId),
        supabase
          .from("capital_contributions")
          .delete()
          .eq("investor_id", userId),
        supabase.from("portfolios").delete().eq("investor_id", userId),
        supabase.from("reports").delete().eq("investor_id", userId),
        // investor_profiles uses user_id
        supabase.from("investor_profiles").delete().eq("user_id", userId),
      ];

      for (const q of deletes) {
        const { error } = await q;
        if (error) throw error;
      }

      // Remove user row from app DB
      const { error: delUserError } = await supabase
        .from("users")
        .delete()
        .eq("id", userId);

      if (delUserError) throw delUserError;

      // Remove auth user via Edge Function admin-delete-user
      // This requires the caller to have an admin session (handled inside adminDeleteUser)
      await db.adminDeleteUser(userId);

      return true;
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("deleteInvestor failed", {
        userId,
        err: err?.message || err,
      });
      throw err;
    }
  },

  async changePassword(currentPassword: string, newPassword: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.email) throw new Error("No user found");
  
  // Re-authenticate with current password
  const { error: reauthError } = await supabase.auth.signInWithPassword({
    email: user.email,
    password: currentPassword,
  });
  
  if (reauthError) throw new Error("Current password is incorrect");
  
  // Update to new password
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  
  if (error) throw error;
  
  // Disconnect all other sessions (different browsers, devices, etc.)
  const { error: signOutError } = await supabase.auth.signOut({
    scope: 'global',
  });
  
  if (signOutError) {
    // Log but don't throw - password was already changed
    console.error("Failed to sign out other sessions:", signOutError);
  }
  
  return { success: true, allSessionsTerminated: !signOutError };
}
};
